/**
 * src/hooks/useLightningPurchase.js
 *
 * Manages the full Lightning film roll purchase flow:
 *   1. Request invoice from /api/invoice
 *   2. Poll /api/status until paid | expired | timeout
 *   3. Store JWT token in IndexedDB on success
 *   4. Return state + controls to the component
 */

import { useState, useRef, useCallback } from "react";
import { saveToken } from "../lib/tokenStore.js";

const POLL_INTERVAL_MS = 2_000;  // check every 2 seconds
const POLL_TIMEOUT_MS  = 600_000; // stop after 10 minutes

export function useLightningPurchase({ onSuccess }) {
  const [state, setState] = useState("idle");
  // idle | loading | awaiting_payment | paid | expired | error

  const [invoice, setInvoice] = useState(null);   // bolt11
  const [paymentHash, setPaymentHash] = useState(null);
  const [currentStyleId, setCurrentStyleId] = useState(null);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  // ── Stop polling ─────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current)   clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollRef.current = null;
    timeoutRef.current = null;
  }, []);

  // ── Cancel / reset ───────────────────────────────────────────────────────────
  const cancel = useCallback(() => {
    stopPolling();
    setState("idle");
    setInvoice(null);
    setPaymentHash(null);
    setCurrentStyleId(null);
    setError(null);
  }, [stopPolling]);

  // ── Poll payment status ──────────────────────────────────────────────────────
  const startPolling = useCallback((hash, styleId) => {
    // Hard timeout — stop polling after 10 minutes
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setState("expired");
    }, POLL_TIMEOUT_MS);

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch("/api/status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentHash: hash, styleId }),
        });
        const data = await res.json();

        if (data.status === "paid") {
          stopPolling();
          // Persist JWT token to IndexedDB
          await saveToken(styleId, data.token, data.shots);
          setState("paid");
          onSuccess?.(styleId, data.shots);

        } else if (data.status === "expired") {
          stopPolling();
          setState("expired");
        }
        // "pending" → keep polling

      } catch (err) {
        console.error("[useLightningPurchase] Poll error:", err);
        // Don't stop on network hiccup — keep trying
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, onSuccess]);

  // ── Initiate purchase ────────────────────────────────────────────────────────
  const purchase = useCallback(async (styleId) => {
    cancel(); // reset any existing state
    setState("loading");
    setCurrentStyleId(styleId);

    try {
      const res = await fetch("/api/invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ styleId }),
      });

      if (!res.ok) throw new Error(`Invoice request failed: ${res.status}`);

      const data = await res.json();

      setInvoice(data.invoice);
      setPaymentHash(data.paymentHash);
      setState("awaiting_payment");

      // Try WebLN auto-pay (Alby / NWC browser extension)
      if (typeof window.webln !== "undefined") {
        try {
          await window.webln.enable();
          await window.webln.sendPayment(data.invoice);
          // WebLN paid — polling will catch the settlement
        } catch {
          // WebLN failed or user cancelled — fall back to QR
        }
      }

      startPolling(data.paymentHash, styleId);

    } catch (err) {
      console.error("[useLightningPurchase] Purchase error:", err);
      setError(err.message || "Failed to create invoice");
      setState("error");
    }
  }, [cancel, startPolling]);

  return {
    state,           // "idle" | "loading" | "awaiting_payment" | "paid" | "expired" | "error"
    invoice,         // bolt11 string — render as QR
    currentStyleId,
    error,
    purchase,        // call with styleId to start
    cancel,
  };
}
